import React, { useState, useEffect } from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import supabase from "../supabaseClient";

const SPORTS = [
  "Baseball", "Softball", "Soccer", "Basketball", "Football",
  "Hockey", "Lacrosse", "Tennis", "Swimming", "Track", "Volleyball", "Wrestling",
];

const VenuesTab = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [sports, setSports] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { loadVenues(); }, []);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("venues").select("*").order("name");
      if (error) throw error;
      setVenues(data || []);
    } catch (err) {
      console.error("Error loading venues:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || !address.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("venues")
          .update({ name: name.trim(), address: address.trim(), sports })
          .eq("id", editingId);
        if (error) throw error;
        setVenues((prev) =>
          prev.map((v) => v.id === editingId ? { ...v, name: name.trim(), address: address.trim(), sports } : v)
        );
      } else {
        const { data, error } = await supabase
          .from("venues")
          .insert([{ name: name.trim(), address: address.trim(), sports }])
          .select()
          .single();
        if (error) throw error;
        setVenues((prev) => [...prev, data]);
      }
      setName("");
      setAddress("");
      setSports([]);
      setEditingId(null);
    } catch (err) {
      console.error("Error saving venue:", err);
      alert("Error saving venue.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (v) => { setEditingId(v.id); setName(v.name); setAddress(v.address); setSports(v.sports || []); };
  const handleCancel = () => { setEditingId(null); setName(""); setAddress(""); setSports([]); };
  const toggleSport = (sport) => setSports((prev) => prev.includes(sport) ? prev.filter((s) => s !== sport) : [...prev, sport]);

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this venue?")) return;
    setVenues((prev) => prev.filter((v) => v.id !== id));
    try {
      const { error } = await supabase.from("venues").delete().eq("id", id);
      if (error) throw error;
    } catch (err) {
      console.error("Error deleting venue:", err);
      loadVenues();
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Save fields, gyms, and parks here. You can attach them to events and games for quick directions.
      </p>

      <form onSubmit={handleSave} className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
        <h4 className="text-sm font-medium text-gray-700">{editingId ? "Edit Venue" : "Add Venue"}</h4>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Venue name (e.g. Riverside Soccer Fields)"
          required
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Full address for directions"
          required
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <div>
          <p className="text-xs text-gray-500 mb-1.5">Sports (optional — leave blank to show for all seasons)</p>
          <div className="flex flex-wrap gap-1.5">
            {SPORTS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleSport(s)}
                className={`px-2 py-0.5 rounded-full text-xs font-medium border touch-manipulation ${
                  sports.includes(s)
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-gray-500 border-gray-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {editingId && (
            <button type="button" onClick={handleCancel}
              className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-300 touch-manipulation">
              Cancel
            </button>
          )}
          <button type="submit" disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 touch-manipulation disabled:opacity-50">
            <Plus size={14} />
            {editingId ? "Save" : "Add Venue"}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
      ) : venues.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">No venues yet.</p>
      ) : (
        <div className="space-y-2">
          {venues.map((v) => (
            <div key={v.id} className="flex items-start gap-3 bg-white border border-gray-100 rounded-lg px-3 py-2.5">
              <MapPin size={16} className="text-gray-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{v.name}</p>
                <p className="text-xs text-gray-400 truncate">{v.address}</p>
                {v.sports?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {v.sports.map((s) => (
                      <span key={s} className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded-full text-xs">{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => handleEdit(v)}
                className="text-xs text-purple-600 hover:text-purple-800 touch-manipulation px-2 py-1">
                Edit
              </button>
              <button onClick={() => handleDelete(v.id)}
                className="text-gray-300 hover:text-red-400 touch-manipulation p-1">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VenuesTab;
